// Invoice reconcile-status helpers — pure, shared between the FE
// `InvoiceLineItems` section pill and the `InvoiceKpiBand` progress
// stat. Single definition of "correctly matched" so the band can't
// disagree with the section.

import { moneyEq, sumAmounts } from '../../calc.ts';
import type { InvoiceLineItem } from '@shared/types/invoice.ts';

/**
 * A line item counts as "correctly matched" when it has at least one
 * link AND the linked rows' aggregate net + gross both equal the line
 * item's stored totals within the standard ±0.01 money tolerance.
 *
 * Partial-only matches (links exist but sums diverge) are excluded —
 * that's the signal the InvoiceLineItems mismatch warning surfaces.
 */
export function isLineCorrectlyMatched(li: InvoiceLineItem): boolean {
  if (li.links.length === 0) return false;
  const linked = sumAmounts(li.links.map((l) => l.pricing));
  return moneyEq(linked.net,   li.pricing.net_amount)
      && moneyEq(linked.gross, li.pricing.gross_amount);
}

export interface InvoiceReconcileStats {
  total:   number;
  matched: number;
  /** matched / total, 0–100. Returns 0 when total === 0. */
  pct:     number;
}

export function invoiceReconcileStats(lineItems: InvoiceLineItem[]): InvoiceReconcileStats {
  const total   = lineItems.length;
  const matched = lineItems.filter(isLineCorrectlyMatched).length;
  const pct     = total > 0 ? Math.round((matched / total) * 100) : 0;
  return { total, matched, pct };
}
